<script setup lang="ts">
import { useCmsTranslations } from "@shopware/composables";
import {
  useAddToCart,
  useNotifications,
  useCartNotification,
  useCartErrorParamsResolver,
} from "#imports";
import { toRefs, computed } from "vue";
import { defu } from "defu";
import type { Schemas } from "#shopware";
import { getCmsTranslate } from "@shopware/helpers";
import { addToCartTracking } from "~/components/utilities/GtmTrackings";

const { pushSuccess, pushError, pushWarning } = useNotifications();
const { getErrorsCodes } = useCartNotification();
const { resolveCartError } = useCartErrorParamsResolver();
const props = defineProps<{
  product: Schemas["Product"];
  tierQuantity?: number;
}>();
const { t } = useI18n();

type Translations = {
  errors: {
    [key: string]: string;
  };
};

let translations: Translations = {
  errors: {
    "product-stock-reached": t("cart.quantityExceededMsg"),
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const { product, tierQuantity } = toRefs(props);
const { addToCart, quantity } = useAddToCart(product);

const isAddToCartProcessing = ref(false);
const addToCartProxy = async () => {
  isAddToCartProcessing.value = true;
  addToCartTracking(product.value, quantity.value);
  await addToCart();
  const errors = getErrorsCodes();
  errors?.forEach((element) => {
    const { messageKey, params } = resolveCartError(element);

    if (messageKey === "product-stock-reached") {
      pushError(
        t("cart.quantityExceededMsg", {
          name: params?.name,
          quantity: params?.quantity,
        })
      );
    } else {
      pushError(getCmsTranslate(translations.errors[messageKey], params));
    }
    isAddToCartProcessing.value = false;
  });

  if (!errors.length)
    pushSuccess(
      `${props.product?.translated.name}` +
        " " +
        t("productDetails.messages.addedToCart")
    );
  isAddToCartProcessing.value = false;

};

const minPurchase = computed(() => product.value.minPurchase || 1);
const maxPurchase = computed(() => product.value.calculatedMaxPurchase || 1);
const priceHidden = computed(
  () =>
    product.value?.translated?.customFields?.custom_product_fields_hide_price
);
const isConfigurable = computed(
  () =>
    product.value?.translated?.customFields?.custom_product_fields_configurator
);

quantity.value = minPurchase.value;

const increaseQuantity = () => {
  if (quantity.value < maxPurchase.value) {
    quantity.value += product.value?.purchaseSteps || 1;
  }
};

const decreaseQuantity = () => {
  if (quantity.value > minPurchase.value) {
    quantity.value -= product.value?.purchaseSteps || 1;
  }
};

const showConfiguration = ref(false);

const isQuotationShown = ref(false);

const showConfigurator = () => {
  showConfiguration.value = true;
};

const showQuotation = () => {
  isQuotationShown.value = true;
};

const openQuotation = () => {
  isQuotationShown.value = true;
};
watch(
  () => tierQuantity.value,
  (newVal) => {
    if (newVal !== null) {
      if (newVal <= maxPurchase.value) {
        quantity.value = newVal;
      } else {
        pushWarning(
          t("cart.quantityExceededMsg", {
            name: product.value?.name,
            quantity: maxPurchase.value,
          })
        );
      }
    }
  }
);
</script>

<template>
  <div
    class="quantity inline-flex flex-wrap items-center gap-x-2.5 [&>button]:px-[1.2rem] mobile:[&>button]:grow mobile:[&>input]:grow [&>button]:px-[1.125rem] [&>button]:pb-1 [&>button]:pt-2"
  >
    <!-- Minus Button -->
    <button
      id="decrease-btn"
      class="btn-outline btn-minus"
      @click="decreaseQuantity"
      :disabled="quantity <= minPurchase"
    >
      -
    </button>
    <!-- Quantity Display -->
    <input
      id="qty"
      v-model="quantity"
      type="number"
      :min="product.minPurchase || 1"
      :max="product.calculatedMaxPurchase"
      :step="product.purchaseSteps || 1"
      class="appearance-none md:w-14 h-12 rounded text-center !ring-transparent !border-skin-light bg-[transparent] mobile:max-w-[7rem] mobile:shrink-0"
      data-testid="product-quantity"
    />
    <!-- Plus Button -->
    <button
      id="increase-btn"
      class="btn-outline btn-plus"
      @click="increaseQuantity"
      :disabled="quantity >= maxPurchase"
    >
      +
    </button>

    <button
      v-if="isConfigurable"
      :disabled="!product.available"
      class="btn flex-1 h-12 cursor-pointer"
      :class="{
        'opacity-50 cursor-not-allowed':
          !product.available || isAddToCartProcessing,
      }"
      data-testid="add-to-cart-button"
      @click="showConfigurator"
    >
      {{ $t("product.configurator.title") }}
      <span class="ml-2">
        <i
          v-if="showConfiguration"
          class="fa-solid fa-spinner animate-spin"
        ></i>
      </span>
    </button>

    <button
      v-else-if="priceHidden"
      class="btn flex-1 h-12 cursor-pointer"
      @click="showQuotation"
    >
      {{ $t("product.quotation.label") }}
    </button>

    <button
      v-else
      :disabled="!product.available"
      class="btn flex-0 lg:flex-1 mt-3 lg:mt-0 h-12"
      :class="{
        'opacity-50 cursor-not-allowed':
          !product.available || isAddToCartProcessing,
      }"
      data-testid="add-to-cart-button"
      @click="addToCartProxy"
    >
      {{ $t("product.addToCart") }}

      <span class="ml-2">
        <i
          v-if="isAddToCartProcessing"
          class="fa-solid fa-spinner animate-spin"
        ></i>
      </span>
    </button>

    <ProductDetailConfiguration
      v-show="showConfiguration"
      :quantity="quantity"
      @open-quotation="openQuotation"
      @close="showConfiguration = false"
    />
    <ProductDetailRequestQuotation
      v-show="isQuotationShown"
      :quantity="quantity"
      @close="isQuotationShown = false"
    />
  </div>
</template>
